/**
 * TempDataDisplay.js
 *
 * This component displays weather station data in a user-friendly format.
 * It handles two types of weather stations:
 * 1. AWS (Automatic Weather Station) - More comprehensive weather data
 * 2. ITB - Basic weather data
 *
 * The component fetches data using the useTemporaryData hook and displays it in a responsive grid layout.
 * All times are displayed in Eastern Standard Time (EST).
 */

import React from "react";
import { useParams } from "react-router-dom";
import { useTemporaryData } from "../../hooks/useWeatherData";
import { Box, Paper, Typography, CircularProgress, Alert } from "@mui/material";

/**
 * Converts Unix timestamp to Eastern Standard Time (EST) format
 * @param {string} unixtime - Unix timestamp in seconds
 * @returns {string} Formatted date string in EST
 */
export const formatToEST = (unixtime) => {
  const date = new Date(parseInt(unixtime) * 1000);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(date);
};

/**
 * Determines the type of weather station based on its unique identifier
 * @param {string} uid - Unique identifier of the weather station
 * @returns {string} Station type: 'aws' or 'itb'
 */
const getStationType = (uid) => {
  // AWS station identifiers
  const awsStationPrefixes = ["ZAA", "TMPC", "TVKP"];

  if (awsStationPrefixes.some((prefix) => uid.startsWith(prefix))) {
    return "aws";
  }

  // Default to ITB station
  return "itb";
};

/**
 * Main component for displaying weather station data
 * Features:
 * - Responsive grid layout
 * - Loading state handling
 * - Error handling
 * - Different data display for AWS and ITB stations
 */
const TempDataDisplay = () => {
  // Get station ID from URL parameters
  const { uid } = useParams();
  // Fetch weather data using custom hook
  const { tempData, loading, error } = useTemporaryData(uid);

  /**
   * Renders the weather data display based on the station type
   * @returns {JSX.Element} Weather data display component
   */
  const renderDataDisplay = () => {
    if (!tempData) return null;

    if (tempData.items && tempData.items.length === 0) {
      return (
        <Alert severity="info">No data found for the selected criteria</Alert>
      );
    }

    const stationType = getStationType(uid);

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Count: {tempData.count}
        </Typography>
        <Box sx={{ mt: 2 }}>
          {tempData.items.map((item, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2, backgroundColor: "#f5f5f5" }}>
              {/* Time Display Section */}
              <Box sx={{ mb: 2, borderBottom: "1px solid #e0e0e0", pb: 1 }}>
                <Typography
                  variant="h6"
                  color="primary.main"
                  sx={{ fontWeight: 500 }}
                >
                  {formatToEST(item.unixtime)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Unix Time: {item.unixtime}
                </Typography>
              </Box>

              {/* Data Grid Layout - Responsive design */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {stationType === "aws" ? (
                  // AWS Station Data Display
                  // Includes comprehensive weather metrics
                  <>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Internal Temperature
                      </Typography>
                      <Typography>{item.internal_temp}°C</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        External Temperature
                      </Typography>
                      <Typography>{item.external_temp}°C</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Internal Humidity
                      </Typography>
                      <Typography>{item.internal_humidity}%</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        External Humidity
                      </Typography>
                      <Typography>{item.external_humidity}%</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Internal Pressure
                      </Typography>
                      <Typography>{item.internal_pressure} hPa</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Wind Data
                      </Typography>
                      <Typography>
                        Speed: {item.wind_speed} km/s | Direction:{" "}
                        {item.wind_direction}°
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Wind Gust
                      </Typography>
                      <Typography>
                        Speed: {item.wind_gust_speed} km/s | Direction:{" "}
                        {item.wind_gust_direction}°
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Orientation
                      </Typography>
                      <Typography>
                        Pitch: {item.pitch}° | Roll: {item.roll}°
                      </Typography>
                    </Box>
                  </>
                ) : (
                  // ITB Station Data Display
                  // Basic weather metrics
                  <>
                    <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Location
                      </Typography>
                      <Typography>
                        {item.payload_latitude}, {item.payload_longitude}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Temperature
                      </Typography>
                      <Typography>{item.internal_temp}°C</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Humidity
                      </Typography>
                      <Typography>{item.internal_humidity}%</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Pressure
                      </Typography>
                      <Typography>{item.internal_pressure} hPa</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Orientation
                      </Typography>
                      <Typography>
                        Pitch: {item.pitch}° | Roll: {item.roll}°
                      </Typography>
                    </Box>
                  </>
                )}
                {/* Common fields for both station types */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    System
                  </Typography>
                  <Typography>Voltage: {item.voltage}V</Typography>
                </Box>
                <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    SBD Message
                  </Typography>
                  <Typography
                    sx={{
                      wordBreak: "break-all",
                      overflowWrap: "break-word",
                    }}
                  >
                    {item.sbd_message || "-"}
                  </Typography>
                </Box>
                <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    SBD Message (HEX)
                  </Typography>
                  <Typography
                    sx={{
                      wordBreak: "break-all",
                      overflowWrap: "break-word",
                    }}
                  >
                    {item.sbd_message_hex || "-"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  };

  // Main component render
  return (
    <Box m={4}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Temporary Data for Station: {uid}
        </Typography>

        {/* Error handling */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading state */}
        {loading ? (
          <Box display="flex" justifyContent="center" m={4}>
            <CircularProgress />
          </Box>
        ) : (
          renderDataDisplay()
        )}
      </Paper>
    </Box>
  );
};

export default TempDataDisplay;
